import { useMutation } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import { useDraftMode } from "../contexts/DraftModeContext";
import { ChangesSource, ChangeType, type NurseInShift, NurseInShiftCreationSource } from "../utils/models";
import { usePublishChanges } from "./usePublishChanges";
import { useScheduleDayOf } from "./useScheduleDayOf";
import { useScheduleSaves } from "./useScheduleSaves";

type ReplaceRequestParams = {
	nurseToAdd: NurseInShift;
	nurseToRemove: NurseInShift;
	requestId?: string;
	/** Sent as the removal's date: the request's shift start, as before. */
	date: DateTime;
	accepted_start_time?: DateTime;
	accepted_end_time?: DateTime;
};

export function useHandleRequest() {
	const { afterScheduleWrite, afterPublish, oldListSlotOf } = useScheduleSaves();
	const scheduleDayOf = useScheduleDayOf();
	const { mutateAsync: publishChanges } = usePublishChanges();
	const { isDraftMode } = useDraftMode();

	const { mutateAsync: decline } = useMutation({
		mutationFn: async ({ requestId, nurseIds }: { requestId: string; nurseIds: number[] }) => {
			return await publishChanges({
				accepted_requests: {},
				declined_requests: { [requestId]: nurseIds },
				actual_changes: [],
				changes: [],
				source: ChangesSource.Staffing,
			});
		},
		onSuccess: (response) => {
			// A decline changes no shift itself, so a reload it asks for has no known dates.
			if (response.shouldRefresh) void afterScheduleWrite({ allWeeks: true });
		},
	});

	const {
		mutateAsync: replaceRequest,
		isPending: isReplacing,
		error: replaceRequestError,
	} = useMutation({
		mutationFn: async ({ nurseToAdd, nurseToRemove, requestId, date, accepted_start_time, accepted_end_time }: ReplaceRequestParams) => {
			const scheduleDay = scheduleDayOf(nurseToAdd);
			const slot = oldListSlotOf(scheduleDay.date, scheduleDay.shiftType);
			// DEFAULT: -1 for a day outside the loaded weeks; the server requires these fields but never reads them.
			const slotFields = { shiftIndex: slot?.shiftIndex ?? -1, weekIndex: slot?.weekIndex ?? -1, dayIndex: slot?.dayIndex ?? -1 };
			const changes = [
				{
					nurse: {
						...nurseToRemove,
						isPending: false,
					},
					...slotFields,
					shiftType: nurseToRemove.shiftType,
					date,
					departmentId: nurseToRemove.departmentId,
					changeType: ChangeType.Delete,
				},
				{
					nurse: {
						...nurseToAdd,
						isPending: false,
					},
					...slotFields,
					shiftType: nurseToAdd.shiftType,
					date,
					departmentId: nurseToAdd.departmentId,
					changeType: ChangeType.Insert,
				},
			];

			const updateRequestBody = {
				accepted_requests: {},
				declined_requests: {},
				actual_changes: [],
				changes,
				source: ChangesSource.Staffing,
				creationSource: NurseInShiftCreationSource.Requests,
				accepted_start_time: accepted_start_time,
				accepted_end_time: accepted_end_time,
			};

			if (requestId) {
				updateRequestBody.accepted_requests = {
					[requestId]: [nurseToAdd.nurseId],
				};
			}

			if (!isDraftMode) {
				const response = await publishChanges(updateRequestBody);
				void afterPublish(response.shouldRefresh, [scheduleDay.date]);
				return response;
			}
			return { shouldRefresh: false };
		},
	});

	return {
		decline,
		replaceRequest,
		isLoading: {
			replaceRequest: isReplacing,
		},
		error: {
			replaceRequest: replaceRequestError,
		},
	};
}
